import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import chevronGreyIcon from 'assets/images/icons/chevron-grey.svg';
import SingleCheckBoxField from './SingleCheckBoxField';
import FormButton from './FormButton';

const MultiCheckboxField = ({
  name,
  staticText,
  autoClose,
  toggleClasses,
  drop,
  align,
  options,
  value,
  handleConfirm,
  disabled,
  allowEmpty,
}) => {
  const { t } = useTranslation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);

  const handleToggleDropdown = () => setShowDropdown(!showDropdown);

  // ----uncomment to enable (select-all checkbox)----
  // const handleSelectAll = () => {
  //   if (selectedOptions.length === options.length) {
  //     setSelectedOptions([]);
  //   } else {
  //     setSelectedOptions(options.map((option) => option.value));
  //   }
  // };

  const handleCheckboxChange = (value) => {
    setSelectedOptions((prev) => {
      if (prev.includes(value)) {
        return prev.filter((item) => item !== value);
      } else {
        return [...prev, value];
      }
    });
  };

  const getSelectedLabelText = () => {
    const defaultText = 'select_select';
    if (!selectedOptions?.length) return defaultText;
    const matchingOptions = options.filter((option) => selectedOptions.includes(option.value));
    if (!matchingOptions.length) return defaultText;
    return matchingOptions.length === 1
      ? matchingOptions[0].displayValue
      : `${matchingOptions[0].displayValue},...`;
  };

  const handleDone = () => {
    // sort output w.r.t options list
    const orderedSelectedOptions = options
      .filter((opt) => selectedOptions.includes(opt.value))
      .map((opt) => opt.value);
    handleConfirm(name, orderedSelectedOptions);
    setShowDropdown(false);
  };

  useEffect(() => {
    setSelectedOptions(value ?? []);
  }, [value]);

  // Reset to parent value when dropdown closes
  useEffect(() => {
    if (!showDropdown) {
      setSelectedOptions(value ?? []);
    }
  }, [showDropdown]);

  return (
    <Dropdown
      show={showDropdown}
      onToggle={handleToggleDropdown}
      className={disabled ? 'disabled' : ''}
      style={staticText ? {} : { width: '6.5rem' }}
      drop={drop ?? 'down'}
      autoClose={autoClose ?? true}
    >
      <Dropdown.Toggle
        id={`dropdown-multiselect`}
        variant='none'
        className={`d-flex justify-content-between rounded-sm cursor-pointer ${
          toggleClasses ?? ''
        }`}
        as='div'
      >
        <>
          <label className='theme-size-0_9 cursor-pointer'>
            {t(staticText || getSelectedLabelText())}
          </label>
          <img src={chevronGreyIcon} className='rotate-180' />
        </>
      </Dropdown.Toggle>

      {showDropdown && (
        <Dropdown.Menu className='px-3 py-3' align={align ?? 'start'} as='div'>
          <>
            {/* <SingleCheckBoxField
              name={`${name}-select-all`}
              value={options.length > 0 && selectedOptions.length === options.length}
              handleChange={handleSelectAll}
              text={<span className='theme-text-green-1'>{t('text_select_all')}</span>}
              classes='mb-3 text-nowrap'
            /> */}

            {options.map((option, index) => (
              <div key={index} className={`custom-check-option`}>
                <SingleCheckBoxField
                  name={`${name}-${index}`}
                  value={selectedOptions.includes(option.value)}
                  handleChange={() => handleCheckboxChange(option.value)}
                  text={<span className='theme-text-green-1'>{option.displayValue}</span>}
                  classes='mb-2 text-nowrap'
                />
              </div>
            ))}
            <div className='d-flex justify-content-end pt-3 border-top mt-2'>
              <FormButton
                text='Done'
                variant='green-1'
                classes=''
                shrink
                minWidth
                onClick={handleDone}
                disabled={!(allowEmpty || selectedOptions.length)}
              />
            </div>
          </>
        </Dropdown.Menu>
      )}
    </Dropdown>
  );
};

MultiCheckboxField.propTypes = {
  name: PropTypes.string.isRequired,
  staticText: PropTypes.string,
  autoClose: PropTypes.bool,
  toggleClasses: PropTypes.string,
  drop: PropTypes.string,
  align: PropTypes.string,
  options: PropTypes.array.isRequired,
  value: PropTypes.array,
  handleConfirm: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  allowEmpty: PropTypes.bool,
};

export default MultiCheckboxField;
